import { useEffect, useMemo, useState } from "react";

import { Controller, type UseFormReturn } from "react-hook-form";
import { MaterialInput } from "../../../../common/components/materialInput/MaterialInput";
import { EMAIL_PATTERN } from "../../../../utils/constants";
import { SimpleSelect } from "../../../../common/components/Select/SimpleSelect";
import { SimpleAutocomplete } from "../../../../common/components/Select/SimpleAutocompleteSelect";
import { useOptimeCountries } from "../CheckoutOptimeCountriesContext";
import { type CheckoutForm } from "../CheckoutCart";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

interface Props {
  control: UseFormReturn<CheckoutForm>["control"];
}

//TODO:
// Hardcoded languages, why?
// Originally added in SCHAEFF-9808
export const OPTIME_LANGUAGES = ["en", "de", "fi", "sv"];

async function makeTimezoneEntries(): Promise<DropdownEntry<string>[]> {
  const { listTimeZones } = await import("timezone-support");

  return listTimeZones()
    .filter((zone) => zone.startsWith("Europe/")) // For MVP we only list european timezones
    .map((zone) => ({
      value: zone,
      label: zone,
      stringLabel: zone,
    }));
}
export const CheckoutOptimeSubscriptionForm = ({ control }: Props) => {
  const { t } = useTranslation();
  const labelT = (suffix: string) => <span>{t(`page.checkout.optime.form.${suffix}`)}*</span>;

  const [timezones, setTimezones] = useState<DropdownEntry<string>[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    makeTimezoneEntries().then((entries) => {
      setTimezones(entries);
      setLoaded(true);
    });
  }, []);

  const languages: DropdownEntry<string>[] = useMemo(() => {
    return OPTIME_LANGUAGES.map((lng) => ({
      value: lng,
      label: t(`languageName.${lng}`),
      stringLabel: t(`languageName.${lng}`),
    }));
  }, [t, t]);

  const countriesRaw = useOptimeCountries().countriesList;

  const countries: DropdownEntry<string>[] = useMemo(
    () =>
      countriesRaw.map((item) => ({
        ...item,
        stringLabel: t(item.stringLabel as string),
        label: t(item.label as string),
      })),
    [countriesRaw, t],
  );

  return (
    <>
      <h2 className={"title-medium mb-3"}>{t(`page.checkout.optime.header.form`)}</h2>

      <div className="mb-2">
        <Controller
          name={"optime.email"}
          control={control}
          rules={{ required: true, pattern: EMAIL_PATTERN }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <MaterialInput
              label={labelT("email")}
              data-test-id={"checkout-optime-input-email"}
              inputRef={ref}
              error={!!error}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className="mb-2">
        <Controller
          name={"optime.name"}
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <MaterialInput
              label={labelT("name")}
              data-test-id={"checkout-optime-input-name"}
              inputRef={ref}
              error={!!error}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className="mb-2">
        <Controller
          name={"optime.lastName"}
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <MaterialInput
              label={labelT("lastName")}
              data-test-id={"checkout-optime-input-last-name"}
              inputRef={ref}
              error={!!error}
              {...inputProps}
            />
          )}
        />
      </div>

      <div className="mb-2">
        <Controller
          name={"optime.plant"}
          control={control}
          rules={{ required: true }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <MaterialInput
              label={labelT("plant")}
              data-test-id={"checkout-optime-input-plant"}
              inputRef={ref}
              error={!!error}
              {...inputProps}
            />
          )}
        />
      </div>

      <div className="mb-2">
        <Controller
          name={"optime.timeZone"}
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) =>
            loaded ? (
              <SimpleSelect
                values={timezones}
                data-test-id={"checkout-optime-select-time-zone"}
                label={labelT("timeZone")}
                error={!!error}
                fullWidth={true}
                onBlur={onBlur}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            ) : (
              <CircleSpinner />
            )
          }
        />
      </div>

      <div className="mb-2">
        <Controller
          name={"optime.language"}
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <SimpleSelect
              values={languages}
              data-test-id={"checkout-optime-select-language"}
              label={labelT("language")}
              error={!!error}
              fullWidth={true}
              onBlur={onBlur}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        />
      </div>

      <div className="mb-2">
        <Controller
          name={"optime.country"}
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <SimpleAutocomplete
              values={countries}
              data-test-id={"checkout-optime-select-country"}
              label={labelT("country")}
              error={!!error}
              fullWidth={true}
              onBlur={onBlur}
              value={value}
              onChange={(value) => onChange(value)}
            />
          )}
        />
      </div>
    </>
  );
};
